import { validateMaintainedGameSpecV1, validateMaintainedConfigV1, maintainedRequirementsV1, type MaintainedGameSpecV1, type MaintainedConfigV1 } from './maintainedProfiles.js';
import { COMPOSED_PROFILE, composedRequirementsV1, validateComposedConfigV1, validateComposedGameSpecV1,
  type ComposedConfigV1, type ComposedGameSpecV1 } from './composedSpec.js';
import { canonicalDigest, canonicalJson, validateSha256, courierShortConfigV1, validatePlatformRunConfigV1, type PlatformRunConfigV1 } from '@djgg/gameplay-kit';
import { AUDIO_MEDIA_FORMATS, audioBundleFilename, type AudioMediaMetadata } from '@djgg/core';
import type { GameQualityRequirementV2 } from '../qualitySchemas.js';
import { COMPOSITION_MEDIA_MAX_WALL_TIME_MS } from '../repos/measuredBuildWindow.js';
import {
  COMPOSITION_MUSIC_SLOT,
  compositionAnimationSlotsV1,
  compositionArtSlotsV1,
  compositionBundleSlotsV1,
  compositionDeferralClosedV1,
  compositionDerivedSlotsV1,
  compositionGeneratedEntitiesV1,
  compositionMediaSlotKindV1,
  compositionMediaSlotsV1,
  compositionPresentationV1,
} from './compositionPresentation.js';

/** Closed maintained profile. Natural-language interpretation is an upstream
 * approval decision, bound by digest rather than claimed as a compiler proof. */
export interface CourierGameSpecV1 {
  schemaVersion: 1;
  profile: 'courier-short@1';
  title: string;
  theme: 'courier' | 'space-post';
}
export const COMPOSITION_IMAGE_MODEL = 'gemini-3.1-flash-lite-image' as const;
export const COMPOSITION_IMAGE_MAX_COST_MICROS = 39_850;
/** Upper bound of one image-CLI batch (the asset-gen step timeout cap). */
export const COMPOSITION_IMAGE_MAX_DURATION_MS = 600_000;
export const COMPOSITION_JUDGE_MODEL = 'gemini-2.5-flash' as const;
export const COMPOSITION_JUDGE_MAX_COST_MICROS = 25_000;
export const COMPOSITION_JUDGE_MAX_DURATION_MS = 30_000;
/** The soundtrack route: one bounded Lyria interaction (no transport retry). */
export const COMPOSITION_MUSIC_MODEL = 'lyria-3.5' as const;
export const COMPOSITION_MUSIC_MAX_COST_MICROS = 80_000;
export const COMPOSITION_MUSIC_MAX_DURATION_MS = 180_000;
/** Judge reservations per judged image: the one strict verdict plus the
 * judge transport's single bounded retry (assets/judge.ts JUDGE_ATTEMPTS). */
export const COMPOSITION_JUDGE_CALLS_PER_IMAGE = 2;
/** Bounded generation attempts per required composition image slot (each
 * sprite, the background, each strip and the cover), the first included. A
 * rejected or imageless slot is regenerated alone — accepted siblings are
 * kept — until it is accepted or its attempts are spent. */
export const COMPOSITION_SLOT_MAX_ATTEMPTS = 3;
export const COMPOSITION_MEDIA_KIND = 'required-media@3' as const;
export const COMPOSITION_MEDIA_MAX_BYTES = 2 * 1024 * 1024;
export const COMPOSITION_ART_STYLE_MAX_LEN = 200;
export const COMPOSITION_ART_SLOT_MAX_LEN = 200;
/** Strip geometry bound: the old pipeline draws at most 4 cells per state. */
export const COMPOSITION_STRIP_MAX_FRAMES = 8;

/** Every accepted composition profile: the closed courier, the maintained
 * exact-variant profiles, and composed (template + data patch, proof-admitted). */
export type CompositionGameSpecV1 = CourierGameSpecV1 | MaintainedGameSpecV1 | ComposedGameSpecV1;
type CompositionSpecV1 = CompositionGameSpecV1;

export interface CompositionMediaExecutionV1 {
  schemaVersion: 2;
  role: 'composition-media@2';
  phase: 'post-structural-proof';
  generation: {
    kind: 'image-generation'; provider: 'gemini'; model: typeof COMPOSITION_IMAGE_MODEL;
    maxCalls: number; maxCostMicrosPerCall: typeof COMPOSITION_IMAGE_MAX_COST_MICROS;
    maxDurationMs: typeof COMPOSITION_IMAGE_MAX_DURATION_MS;
  };
  judge: {
    kind: 'image-judge'; provider: 'gemini'; model: typeof COMPOSITION_JUDGE_MODEL;
    maxCalls: number; maxCostMicrosPerCall: typeof COMPOSITION_JUDGE_MAX_COST_MICROS;
    maxDurationMs: typeof COMPOSITION_JUDGE_MAX_DURATION_MS;
  };
  /** Present exactly when the accepted slots include the soundtrack. */
  music?: {
    kind: 'music-generation'; provider: 'lyria'; model: typeof COMPOSITION_MUSIC_MODEL;
    maxCalls: 1; maxCostMicrosPerCall: typeof COMPOSITION_MUSIC_MAX_COST_MICROS;
    maxDurationMs: typeof COMPOSITION_MUSIC_MAX_DURATION_MS;
  };
  limits: {
    maxProviderCalls: number;
    maxCostMicros: number;
    maxMediaWallTimeMs: typeof COMPOSITION_MEDIA_MAX_WALL_TIME_MS;
  };
}

/** The derived call plan of one full-art build — the ONE place the producer's
 * shape becomes numbers. `imageCalls` is the exact first pass; every image
 * slot may get up to {@link COMPOSITION_SLOT_MAX_ATTEMPTS} − 1 recovery
 * generations of its own: a strip a judge-directed regeneration anchored on
 * its accepted base sprite, a static sprite or the background a
 * judge-directed improve pass, the cover a fresh regeneration. */
export interface CompositionMediaCallPlanV1 {
  /** One style-anchor hero sheet for every fixed-colour sprite (0 or 1). */
  sheetCalls: number;
  /** One isolated call per recoloured (neutral) sprite. */
  isolatedSpriteCalls: number;
  backgroundCalls: 1;
  stripCalls: number;
  coverCalls: 1;
  /** Exact first-pass image generations. */
  imageCalls: number;
  /** Recovery generations across every strip slot. */
  stripRecoveryCalls: number;
  /** Judge-directed improve generations across every static image (each
   * sprite and the background), each regenerated singly from its delta. */
  staticRecoveryCalls: number;
  /** Fresh regenerations of the (unjudged) gallery cover. */
  coverRecoveryCalls: number;
  /** Images the strict judge reviews on the first pass (sprites, background,
   * strips; the gallery cover is not in-play art and is not judged). */
  judgedImages: number;
  musicCalls: 0 | 1;
}

export function compositionMediaCallPlanV1(spec: CompositionSpecV1, options: { music: boolean }): CompositionMediaCallPlanV1 {
  // A derived kind's slots are recoloured from its source's accepted art:
  // they cost no generation and no verdict.
  const entities = compositionGeneratedEntitiesV1(compositionPresentationV1(spec)).map(([, entity]) => entity);
  const neutral = entities.filter(entity => entity.art === 'neutral').length;
  const sheetCalls = entities.length > neutral ? 1 : 0;
  const derived = compositionDerivedSlotsV1(spec);
  const stripCalls = compositionAnimationSlotsV1(spec).filter(strip => !derived.has(strip.slot)).length;
  const recoveries = COMPOSITION_SLOT_MAX_ATTEMPTS - 1;
  return {
    sheetCalls,
    isolatedSpriteCalls: neutral,
    backgroundCalls: 1,
    stripCalls,
    coverCalls: 1,
    imageCalls: sheetCalls + neutral + 1 + stripCalls + 1,
    stripRecoveryCalls: recoveries * stripCalls,
    staticRecoveryCalls: recoveries * (entities.length + 1),
    coverRecoveryCalls: recoveries,
    judgedImages: entities.length + 1 + stripCalls,
    musicCalls: options.music ? 1 : 0,
  };
}

/** Closed paid-media route, DERIVED from the profile presentation in the old
 * pipeline's proven plan shape: one style-locked hero sheet for the fixed-
 * colour sprites, one isolated call per recoloured sprite, one full-scene
 * background, one strip per declared animation state (each conditioned on its
 * kind's sliced base sprite), one asset-referenced cover — every image slot
 * with its own bounded recovery attempts — an optional single soundtrack
 * call, and a bounded judge allowance per judged generation. A release
 * authority cannot choose a larger allowance. */
export function compositionMediaExecutionV1(spec: CompositionSpecV1, options: { music: boolean }): CompositionMediaExecutionV1 {
  const plan = compositionMediaCallPlanV1(spec, options);
  const judgedRecoveryCalls = plan.stripRecoveryCalls + plan.staticRecoveryCalls;
  const generationCalls = plan.imageCalls + judgedRecoveryCalls + plan.coverRecoveryCalls;
  const judgeCalls = COMPOSITION_JUDGE_CALLS_PER_IMAGE * (plan.judgedImages + judgedRecoveryCalls);
  return {
    schemaVersion: 2,
    role: 'composition-media@2',
    phase: 'post-structural-proof',
    generation: {
      kind: 'image-generation', provider: 'gemini', model: COMPOSITION_IMAGE_MODEL,
      maxCalls: generationCalls, maxCostMicrosPerCall: COMPOSITION_IMAGE_MAX_COST_MICROS,
      maxDurationMs: COMPOSITION_IMAGE_MAX_DURATION_MS,
    },
    judge: {
      kind: 'image-judge', provider: 'gemini', model: COMPOSITION_JUDGE_MODEL,
      maxCalls: judgeCalls, maxCostMicrosPerCall: COMPOSITION_JUDGE_MAX_COST_MICROS,
      maxDurationMs: COMPOSITION_JUDGE_MAX_DURATION_MS,
    },
    ...(plan.musicCalls ? { music: {
      kind: 'music-generation' as const, provider: 'lyria' as const, model: COMPOSITION_MUSIC_MODEL,
      maxCalls: 1 as const, maxCostMicrosPerCall: COMPOSITION_MUSIC_MAX_COST_MICROS,
      maxDurationMs: COMPOSITION_MUSIC_MAX_DURATION_MS,
    } } : {}),
    limits: {
      maxProviderCalls: generationCalls + judgeCalls + plan.musicCalls,
      maxCostMicros: generationCalls * COMPOSITION_IMAGE_MAX_COST_MICROS
        + judgeCalls * COMPOSITION_JUDGE_MAX_COST_MICROS
        + plan.musicCalls * COMPOSITION_MUSIC_MAX_COST_MICROS,
      maxMediaWallTimeMs: COMPOSITION_MEDIA_MAX_WALL_TIME_MS,
    },
  };
}

/** The provider route a reservation of `kind` must match, or null. */
export function compositionMediaRouteV1(execution: CompositionMediaExecutionV1, kind: string):
  CompositionMediaExecutionV1['generation'] | CompositionMediaExecutionV1['judge'] | NonNullable<CompositionMediaExecutionV1['music']> | null {
  return kind === 'image-generation' ? execution.generation
    : kind === 'image-judge' ? execution.judge
      : kind === 'music-generation' ? execution.music ?? null
        : null;
}

export async function compositionMediaExecutionDigest(
  execution: CompositionMediaExecutionV1,
): Promise<string> {
  return canonicalDigest(execution);
}

/** Effective child/join boundary. `MeasuredMediaBudgetAuthorityV1.deadlineAt`
 * remains the parent logical deadline for immutable identity; callers must not
 * subtract the finish reserve a second time from this returned value. */
export function compositionMediaDeadlineAt(input: {
  execution: { limits: Pick<CompositionMediaExecutionV1['limits'], 'maxMediaWallTimeMs'> };
  parentDeadlineAt: number;
  finishReserveMs: number;
  releasedAt: number;
}): number {
  if (![input.parentDeadlineAt, input.finishReserveMs, input.releasedAt].every(Number.isSafeInteger)
    || input.parentDeadlineAt < 1 || input.finishReserveMs < 0 || input.releasedAt < 1) {
    throw new Error('Invalid composition media deadline authority');
  }
  return Math.min(
    input.parentDeadlineAt - input.finishReserveMs,
    input.releasedAt + input.execution.limits.maxMediaWallTimeMs,
  );
}

export function validateCompositionMediaExecutionV1(
  value: unknown,
  spec: CompositionSpecV1,
  options: { music: boolean },
): CompositionMediaExecutionV1 {
  record(value, ['schemaVersion', 'role', 'phase', 'generation', 'judge', 'limits',
    ...(options.music ? ['music'] : [])]);
  const routeKeys = ['kind', 'provider', 'model', 'maxCalls', 'maxCostMicrosPerCall', 'maxDurationMs'];
  record(value.generation, routeKeys);
  record(value.judge, routeKeys);
  if (options.music) record(value.music, routeKeys);
  record(value.limits, [
    'maxProviderCalls', 'maxCostMicros', 'maxMediaWallTimeMs',
  ]);
  const expected = compositionMediaExecutionV1(spec, options);
  if (canonicalJson(value) !== canonicalJson(expected)) {
    throw new Error('Unsupported composition media execution authority');
  }
  return structuredClone(expected);
}

/** Creator-approved art direction bound into the accepted authority digest:
 * one shared style line and one appearance line per art slot (each kind's base
 * sprite plus the background; strips and the cover inherit them). The text is
 * bounded DATA, never instructions: it reaches the image provider only through
 * the asset prompt composer, under the provider safety settings and the strict
 * per-image judge — the same review every asset-studio prompt gets. */
export interface CompositionArtDirectionV1 {
  style: string;
  slots: Record<string, string>;
}

function artText(value: unknown, max: number, label: string): string {
  if (typeof value !== 'string' || value.length < 1 || value.length > max || value.trim() !== value
    || /[\u0000-\u001f\u007f<>{}`]/.test(value)) {
    throw new Error(`Invalid composition art direction ${label}`);
  }
  return value;
}

export function validateCompositionArtDirectionV1(value: unknown, slots: readonly string[]): CompositionArtDirectionV1 {
  record(value, ['style', 'slots']);
  const style = artText(value.style, COMPOSITION_ART_STYLE_MAX_LEN, 'style');
  const raw = value.slots;
  record(raw, [...slots]);
  return {
    style,
    slots: Object.fromEntries([...slots].sort().map(slot => [slot, artText(raw[slot], COMPOSITION_ART_SLOT_MAX_LEN, slot)])),
  };
}

/** Cell geometry of a bound animation strip (equal-width cells, one row). */
export interface CompositionStripGeometryV1 {
  frames: number;
  frameWidth: number;
  frameHeight: number;
}
export interface CompositionMediaReceiptImageV1 {
  slot: string; contentSha256: string; bytes: number;
  width: number; height: number; file: string; url: string; jobId?: string; variantId?: string;
  /** Present exactly for a strip slot. */
  strip?: CompositionStripGeometryV1;
}
export interface CompositionMediaReceiptMusicV1 {
  slot: typeof COMPOSITION_MUSIC_SLOT; contentSha256: string; bytes: number;
  file: string; url: string; audio: AudioMediaMetadata; jobId?: string; variantId?: string;
}
/** Why a deferrable slot was published without its own art. */
export type CompositionDeferredMediaReasonV1 =
  /** Every attempt the slot had ended without accepted art. */
  | 'not-accepted'
  /** The media window refused the slot's next dispatch. */
  | 'media-window'
  /** The soundtrack provider explicitly declined the accepted brief. */
  | 'provider-refused'
  /** The soundtrack provider could not answer (rate limit, server fault,
   * timeout or network failure) on the one accepted call. */
  | 'provider-unavailable'
  /** The soundtrack provider rejected the platform's own request (a 4xx
   * that is not a content refusal): a platform bug, logged as one. */
  | 'request-rejected';
/** The reasons a promised SOUNDTRACK may be deferred for: its one provider
 * call ended without music for a reason that is not the game's. The game
 * plays on the shell's local audio, and no follow-up pass retries it. */
export const COMPOSITION_MUSIC_DEFERRAL_REASONS: readonly CompositionDeferredMediaReasonV1[] =
  ['provider-refused', 'provider-unavailable', 'request-rejected'];
export const COMPOSITION_DEFERRED_MEDIA_REASONS: readonly CompositionDeferredMediaReasonV1[] =
  ['not-accepted', 'media-window', ...COMPOSITION_MUSIC_DEFERRAL_REASONS];

/** A slot a composed version publishes WITHOUT its own art. A strip's kind
 * holds its approved still sprite for that state; a deferred background is a
 * themed palette backdrop the shell paints; and a deferred entity sprite uses
 * the shell's compiled built-in visual. Generated artwork is optional and must
 * not block a functional first playable. The gap is recorded here and a
 * follow-up art pass regenerates exactly these slots. Only
 * {@link compositionDeferrableSlotsV1} slots can be deferred; the cover stays
 * mandatory, while a promised soundtrack can be deferred only when its one
 * provider call ends without music for a reason that is not the game's
 * ({@link COMPOSITION_MUSIC_DEFERRAL_REASONS}). A DERIVED slot (a kind
 * whose art is another's, recoloured) is deferred exactly when its source
 * slot is, with the source's reason, and names that source: it is never its
 * own generation job — the follow-up pass regenerates the source and the
 * derivation follows. */
export interface CompositionDeferredMediaV1 {
  slot: string;
  reason: CompositionDeferredMediaReasonV1;
  /** Present exactly on a derived slot: the source slot it follows. */
  source?: string;
}

/** The ONE validator of a deferred-slot list: deferrable accepted slots of
 * this spec (strips, the background, entity sprites) and the derived slots
 * that follow them — each derived slot naming its source, deferred with it
 * and for its reason, and none left behind ({@link compositionDeferralClosedV1})
 * — unique, sorted by slot, known reasons. Returns a canonical copy. */
export function validateCompositionDeferredMediaV1(value: unknown, slots: readonly string[], spec: CompositionGameSpecV1): CompositionDeferredMediaV1[] {
  if (!Array.isArray(value)) throw new Error('Invalid composition deferred media');
  const derived = value.length ? compositionDerivedSlotsV1(spec) : new Map<string, string>();
  const checked = value.map((item): CompositionDeferredMediaV1 => {
    record(item, ['slot', 'reason', ...(item && typeof item === 'object' && Object.hasOwn(item, 'source') ? ['source'] : [])]);
    if (typeof item.slot !== 'string' || item.source !== derived.get(item.slot)
      || !COMPOSITION_DEFERRED_MEDIA_REASONS.includes(item.reason as CompositionDeferredMediaReasonV1)) {
      throw new Error('Invalid composition deferred media');
    }
    return { slot: item.slot, reason: item.reason as CompositionDeferredMediaReasonV1, ...(typeof item.source === 'string' ? { source: item.source } : {}) };
  });
  for (let i = 1; i < checked.length; i++) {
    if (checked[i - 1]!.slot.localeCompare(checked[i]!.slot) >= 0) throw new Error('Invalid composition deferred media');
  }
  const bySlot = new Map(checked.map(item => [item.slot, item]));
  const visualSlots = checked.filter(item => item.slot !== COMPOSITION_MUSIC_SLOT).map(item => item.slot);
  if ((visualSlots.length && !compositionDeferralClosedV1(spec, slots, visualSlots))
    || checked.some(item => (item.slot === COMPOSITION_MUSIC_SLOT) !== COMPOSITION_MUSIC_DEFERRAL_REASONS.includes(item.reason))
    || checked.some(item => item.source !== undefined && bySlot.get(item.source)?.reason !== item.reason)) {
    throw new Error('Invalid composition deferred media');
  }
  return checked;
}

/** The deferred entries of `slots`' derived slots that follow the deferred
 * `sources` (each with its source's reason) — what a producer appends once it
 * knows which source slots it could not accept. Sorted with them. */
export function compositionFollowingDeferralsV1(spec: CompositionGameSpecV1, slots: readonly string[],
  sources: readonly CompositionDeferredMediaV1[]): CompositionDeferredMediaV1[] {
  const following = [...compositionDerivedSlotsV1(spec)].flatMap(([slot, source]) => {
    const deferred = sources.find(item => item.slot === source);
    return deferred && slots.includes(slot) ? [{ slot, reason: deferred.reason, source }] : [];
  });
  return [...sources, ...following].sort((a, b) => a.slot.localeCompare(b.slot));
}

export interface CompositionMediaReceiptV1 {
  schemaVersion: 2; authorityDigest: string; structuralSourceDigest: string;
  closureDigest: string; mediaBasisDigest: string;
  /** Every bound image: sprites, the background and the animation strips. */
  images: CompositionMediaReceiptImageV1[];
  /** The bound soundtrack, exactly when the accepted slots include music. */
  music?: CompositionMediaReceiptMusicV1;
  /** Deferrable slots published without their own art (sorted; absent = none). */
  deferred?: CompositionDeferredMediaV1[];
}

/** Every bound item of a receipt (images, then the soundtrack) — the one
 * enumeration lineage, forks and rebinds walk. */
export function compositionReceiptItemsV1(receipt: Pick<CompositionMediaReceiptV1, 'images' | 'music'>):
  Array<CompositionMediaReceiptImageV1 | CompositionMediaReceiptMusicV1> {
  return [...receipt.images, ...(receipt.music ? [receipt.music] : [])];
}

/** The bundle slots a receipt binds, sorted — compared against
 * {@link compositionBundleSlotsV1} of the accepted slots everywhere. */
export function compositionReceiptSlotsV1(receipt: Pick<CompositionMediaReceiptV1, 'images' | 'music'>): string[] {
  return compositionReceiptItemsV1(receipt).map(item => item.slot).sort();
}

/** True when a receipt accounts for exactly the bundle slots of the accepted
 * media: each one is bound, or is a deferrable slot recorded as deferred —
 * never both. */
export function compositionReceiptCoversV1(receipt: Pick<CompositionMediaReceiptV1, 'images' | 'music' | 'deferred'>, slots: readonly string[],
  spec: CompositionGameSpecV1): boolean {
  if (!Array.isArray(receipt?.images)) return false;
  let deferred: CompositionDeferredMediaV1[];
  try { deferred = validateCompositionDeferredMediaV1(receipt.deferred ?? [], slots, spec); } catch { return false; }
  if (receipt.deferred !== undefined && deferred.length === 0) return false;
  return canonicalJson([...compositionReceiptSlotsV1(receipt), ...deferred.map(item => item.slot)].sort())
    === canonicalJson(compositionBundleSlotsV1(slots).sort());
}

export type CompositionMediaFormatV1 = 'png' | 'jpg';
/** Formats an image slot may bind: sprites and strips are alpha PNGs; the
 * full-scene background is the asset pipeline's opaque JPEG (or a PNG). */
export function compositionMediaFormatsV1(slot: string): readonly CompositionMediaFormatV1[] {
  return compositionMediaSlotKindV1(slot) === 'background' ? ['jpg', 'png'] : ['png'];
}
/** Pixel bounds per slot kind: a sprite accepts the platform's normal ~1 MP
 * image output (including creator-attached carry); a background is a scene;
 * a strip is up to {@link COMPOSITION_STRIP_MAX_FRAMES} cells wide. */
export function compositionMediaDimensionBoundsV1(slot: string): { min: number; max: number } {
  const kind = compositionMediaSlotKindV1(slot);
  return kind === 'background' || kind === 'strip' ? { min: 8, max: 2048 } : { min: 8, max: 1024 };
}
/** The ONE bundle-local name of a bound image. */
export function compositionMediaFileV1(slot: string, contentSha256: string, format: CompositionMediaFormatV1) {
  const name = `${slot}.${contentSha256}.${format}`;
  return { file: `public/djgg-assets/${name}`, url: `./djgg-assets/${name}` };
}
/** The ONE bundle-local name of the bound soundtrack (core's audio naming). */
export function compositionMusicFileV1(contentSha256: string, audio: AudioMediaMetadata) {
  const name = audioBundleFilename(contentSha256, audio.mime);
  return { file: `public/djgg-assets/${name}`, url: `./djgg-assets/${name}` };
}
/** Shared receipt-image identity check (binder, bundle verifier, final proof). */
export function validateCompositionMediaReceiptImageV1(
  image: CompositionMediaReceiptImageV1,
  slots: readonly string[],
): CompositionMediaFormatV1 {
  const format = /\.(png|jpg)$/.exec(typeof image?.file === 'string' ? image.file : '')?.[1] as CompositionMediaFormatV1 | undefined;
  const kind = image && slots.includes(image.slot) ? compositionMediaSlotKindV1(image.slot) : undefined;
  const bounds = kind && kind !== 'music' && kind !== 'cover' ? compositionMediaDimensionBoundsV1(image.slot) : undefined;
  if (!image || !bounds || !format || !compositionMediaFormatsV1(image.slot).includes(format)
    || typeof image.contentSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(image.contentSha256)
    || !Number.isSafeInteger(image.bytes) || image.bytes < 33 || image.bytes > COMPOSITION_MEDIA_MAX_BYTES
    || ![image.width, image.height].every(value => Number.isInteger(value) && value >= bounds.min && value <= bounds.max)) {
    throw new Error('Invalid composition media receipt image');
  }
  if ((kind === 'strip') !== (image.strip !== undefined)) throw new Error('Invalid composition media receipt image');
  if (image.strip) {
    const { frames, frameWidth, frameHeight } = image.strip;
    if (!Number.isSafeInteger(frames) || frames < 1 || frames > COMPOSITION_STRIP_MAX_FRAMES
      || !Number.isSafeInteger(frameWidth) || frameWidth < 1 || !Number.isSafeInteger(frameHeight) || frameHeight < 1
      || image.width !== frames * frameWidth || image.height !== frameHeight) {
      throw new Error('Invalid composition media receipt strip geometry');
    }
  }
  const expected = compositionMediaFileV1(image.slot, image.contentSha256, format);
  if (image.file !== expected.file || image.url !== expected.url) throw new Error('Invalid composition media receipt image');
  return format;
}
/** Shared receipt-music identity check: an Ogg/Opus generated loop inside the
 * platform's music byte/duration ceilings, named by core's audio naming. */
export function validateCompositionMediaReceiptMusicV1(
  music: CompositionMediaReceiptMusicV1,
  slots: readonly string[],
): void {
  const format = AUDIO_MEDIA_FORMATS.music;
  if (!music || music.slot !== COMPOSITION_MUSIC_SLOT || !slots.includes(COMPOSITION_MUSIC_SLOT)
    || typeof music.contentSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(music.contentSha256)
    || !Number.isSafeInteger(music.bytes) || music.bytes < 1 || music.bytes > format.maxBytes
    || !music.audio || music.audio.mime !== 'audio/ogg' || music.audio.bytes !== music.bytes
    // Duration is measured (frames / rate), so it is a finite real, not an integer.
    || !Number.isFinite(music.audio.durationMs) || music.audio.durationMs <= 0 || music.audio.durationMs > format.maxDurationMs) {
    throw new Error('Invalid composition media receipt music');
  }
  const expected = compositionMusicFileV1(music.contentSha256, music.audio);
  if (music.file !== expected.file || music.url !== expected.url) throw new Error('Invalid composition media receipt music');
}

export interface CompositionMediaV1 {
  kind: typeof COMPOSITION_MEDIA_KIND;
  /** Exactly {@link compositionMediaSlotsV1} of the accepted spec. */
  slots: string[];
  artDirection: CompositionArtDirectionV1;
  /** Omitted by local/synthetic authorities, which remain zero-provider. */
  execution?: CompositionMediaExecutionV1;
}

export interface AcceptedCompositionV1 {
  /** Required generated presentation. Absence is the coded presentation. */
  media?: CompositionMediaV1;
  schemaVersion: 1;
  spec: CompositionGameSpecV1;
  intent: {
    inputDigest: string;
    settingsDigest: string;
    designDigest: string;
    requirements: GameQualityRequirementV2[];
  };
  compilerRevision: 'courier-compiler@1';
  compilerDigest: string;
  config: PlatformRunConfigV1 | MaintainedConfigV1 | ComposedConfigV1;
  ownershipRevision: 'closed-composition@1';
  closureDigest: string;
  /** Immutable content-addressed release entry. Omitted only by local legacy
   * fixtures; released admission requires this field. */
  runtimeReleaseId?: string;
  digest: string;
}

export function courierRequirementsV1(): GameQualityRequirementV2[] {
  const rules: Array<[string, GameQualityRequirementV2['semantic']['kind'], string[], string[]]> = [
    ['run-jump', 'motion', ['run', 'jump'], ['fixed-step-solid-contact']],
    ['collect-all-exit', 'progression', ['collect', 'exit'], ['three-distinct-parcels', 'score-800', 'winner-seat-0']],
    ['loss-restart', 'lifecycle', ['lose', 'restart'], ['three-lives', 'timeout-1200', 'fresh-state']],
    ['native-controls', 'agency', ['run', 'jump'], ['desktop', 'touch']],
  ];
  return rules.map(([id, kind, actions, consequences]) => ({
    id, mandatory: true, provenance: { source: 'approved-design', reference: 'courier-short@1' },
    semantic: { kind, revision: 1, parameters: {} },
    actors: ['runner'], targets: [], preconditions: [], actions, consequences,
    forbiddenOutcomes: ['requirement-omitted'],
    obligations: [{ stage: 'final', evidenceKind: 'deterministic-simulation', device: 'deterministic' },
      ...(id === 'native-controls' ? [
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'desktop' as const },
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'touch' as const },
      ] : [])],
  }));
}

function record(value: unknown, keys: string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) throw new Error('Invalid composition record');
}
export function validateCourierGameSpecV1(value: unknown): CourierGameSpecV1 {
  record(value, ['schemaVersion','profile','title','theme']);
  if (value.schemaVersion !== 1 || value.profile !== 'courier-short@1'
    || typeof value.title !== 'string' || value.title.length < 1 || value.title.length > 80
    || /[\u0000-\u001f<>]/.test(value.title) || value.title.trim() !== value.title
    || !['courier','space-post'].includes(value.theme as string)) throw new Error('Unsupported composition specification');
  return structuredClone(value) as unknown as CourierGameSpecV1;
}
export async function acceptCompositionV1(input: Omit<AcceptedCompositionV1, 'digest'>): Promise<AcceptedCompositionV1> {
  record(input, ['schemaVersion','spec','intent','compilerRevision','compilerDigest','config','ownershipRevision','closureDigest',
    ...(Object.hasOwn(input,'runtimeReleaseId') ? ['runtimeReleaseId'] : []), ...(Object.hasOwn(input,'media') ? ['media'] : [])]);
  record(input.intent, ['inputDigest','settingsDigest','designDigest','requirements']);
  if (input.schemaVersion !== 1 || input.compilerRevision !== 'courier-compiler@1'
    || input.ownershipRevision !== 'closed-composition@1') throw new Error('Unsupported composition authority');
  const spec = validateCompositionGameSpecV1(input.spec);
  let media: CompositionMediaV1 | undefined;
  if (input.media !== undefined) {
    record(input.media, ['kind', 'slots', 'artDirection', ...(Object.hasOwn(input.media, 'execution') ? ['execution'] : [])]);
    // The soundtrack is the one admission-policy slot (the platform music
    // switch); every other slot is derived from the presentation alone.
    const music = Array.isArray(input.media.slots) && input.media.slots.includes(COMPOSITION_MUSIC_SLOT);
    const slots = compositionMediaSlotsV1(spec, { music });
    if (input.media.kind !== COMPOSITION_MEDIA_KIND || !Array.isArray(input.media.slots)
      || canonicalJson(input.media.slots) !== canonicalJson(slots)) throw new Error('Unsupported composition media promise');
    if (input.media.execution !== undefined && !input.runtimeReleaseId) {
      throw new Error('Composition provider execution requires a released runtime authority');
    }
    media = {
      kind: COMPOSITION_MEDIA_KIND,
      slots,
      artDirection: validateCompositionArtDirectionV1(input.media.artDirection, compositionArtSlotsV1(spec)),
      ...(input.media.execution !== undefined
        ? { execution: validateCompositionMediaExecutionV1(input.media.execution, spec, { music }) } : {}),
    };
  }
  const config = spec.profile === 'courier-short@1' ? validatePlatformRunConfigV1(input.config)
    : spec.profile === COMPOSED_PROFILE ? await validateComposedConfigV1(spec, input.config)
      : validateMaintainedConfigV1(spec, input.config);
  if (spec.profile === 'courier-short@1' && canonicalJson(config) !== canonicalJson(courierShortConfigV1())) throw new Error('Unsupported courier mechanics configuration');
  for (const digest of [input.compilerDigest, input.closureDigest, input.runtimeReleaseId,
    input.intent.inputDigest, input.intent.settingsDigest, input.intent.designDigest]) {
    if (digest !== undefined) validateSha256(digest, 'composition');
  }
  if (canonicalJson(input.intent.requirements) !== canonicalJson(compositionRequirementsV1(spec))) throw new Error('Uncovered or changed composition requirement');
  const value = { ...structuredClone(input), spec, config, ...(media ? { media } : {}) };
  return { ...value, digest: await canonicalDigest(value) };
}
export async function validateAcceptedCompositionV1(input: unknown): Promise<AcceptedCompositionV1> {
  record(input, ['schemaVersion','spec','intent','compilerRevision','compilerDigest','config','ownershipRevision','closureDigest','digest',
    ...(input && typeof input === 'object' && Object.hasOwn(input,'runtimeReleaseId') ? ['runtimeReleaseId'] : []),
    ...(input && typeof input === 'object' && Object.hasOwn(input,'media') ? ['media'] : [])]);
  const { digest, ...body } = input;
  const checked = await acceptCompositionV1(body as unknown as Omit<AcceptedCompositionV1, 'digest'>);
  if (checked.digest !== digest) throw new Error('Composition authority digest mismatch');
  return checked;
}
/** Cosmetic proposals create a new candidate, never replace accepted intent. */
export function proposeCourierPresentationV1(spec: CourierGameSpecV1 | MaintainedGameSpecV1, proposal: unknown): CourierGameSpecV1 {
  validateCourierGameSpecV1(spec);
  record(proposal, ['title','theme']);
  return validateCourierGameSpecV1({ ...spec, ...proposal });
}

export function validateCompositionGameSpecV1(value: unknown): CompositionGameSpecV1 {
  const profile = value && typeof value === 'object' ? Object.getOwnPropertyDescriptor(value, 'profile')?.value : undefined;
  return profile === 'courier-short@1' ? validateCourierGameSpecV1(value)
    : profile === COMPOSED_PROFILE ? validateComposedGameSpecV1(value) : validateMaintainedGameSpecV1(value);
}

/** The ONE requirement derivation per accepted spec. */
export function compositionRequirementsV1(spec: CompositionGameSpecV1): GameQualityRequirementV2[] {
  return spec.profile === 'courier-short@1' ? courierRequirementsV1()
    : spec.profile === COMPOSED_PROFILE ? composedRequirementsV1(spec) : maintainedRequirementsV1(spec);
}
/** Preserve the original closed courier API; multi-profile callers explicitly
 * choose acceptCompositionV1. The compiler revision is a historical protocol
 * identifier; the exact compiler digest, not its label, pins executable code. */
export type AcceptedCourierCompositionV1 = Omit<AcceptedCompositionV1, 'spec' | 'config'> & {spec: CourierGameSpecV1; config: PlatformRunConfigV1};
export async function acceptCourierCompositionV1(input: Omit<AcceptedCompositionV1, 'digest'>): Promise<AcceptedCourierCompositionV1> {
  validateCourierGameSpecV1(input.spec);
  return await acceptCompositionV1(input) as AcceptedCourierCompositionV1;
}
